/**
 * File: AppSidebar.tsx
 * Path: apps/admin/src/modules/layout/components/AppSidebar.tsx
 * Description: Master Sidebar utilizing tokenized system spacing and sharp editorial geometry.
 */

import { ADMIN_PATHS, AdminNavItem } from "@idk/config";
import { useAppSidebarController } from "@/modules/layout/hooks/useAppSidebarController";
import { useNavItemController } from "@/modules/layout/hooks/useNavItemController";
import { useNavGroupController } from "@/modules/layout/hooks/useNavGroupController";
import { BooksPicker } from "@/shared/components/BooksPrimitive";
import { UnreadBadge } from "@/modules/layout/components/UnreadBadge";
import { AnimatePresence, motion } from "framer-motion";
import {
  BookOpen,
  Wallet,
  Banknote,
  BarChart3,
  Repeat,
  Activity,
  Bell,
  Briefcase,
  ChevronDown,
  FileSignature,
  FileText,
  FolderOpen,
  GraduationCap,
  Handshake,
  HelpCircle,
  Home,
  LogOut,
  ListTree,
  LucideIcon,
  Package,
  Palette,
  Scale,
  Target,
  Landmark,
  Building2,
  Search,
  Settings,
  Share2,
  SlidersHorizontal,
  Sparkles,
  Star,
  Users,
  Wrench,
} from "lucide-react";
import React from "react";
import { NavLink } from "react-router-dom";

const luxuryEase = [0.16, 1, 0.3, 1] as const;

const IconMap: Record<string, LucideIcon> = {
  Activity,
  Bell,
  Home,
  FolderOpen,
  GraduationCap,
  Handshake,
  Users,
  Wrench,
  Settings,
  Briefcase,
  Package,
  Search,
  FileText,
  FileSignature,
  Palette,
  Share2,
  Sparkles,
  Star,
  HelpCircle,
  BookOpen,
  Wallet,
  Banknote,
  BarChart3,
  Repeat,
  ListTree,
  Scale,
  Target,
  Landmark,
  Building2,
  SlidersHorizontal,
};

const NavItem = ({ item, isOpen }: { item: AdminNavItem; isOpen: boolean }) => {
  const { isActive } = useNavItemController(item.href);
  if (!item.href) return null;

  const Icon = item.icon && IconMap[item.icon] ? IconMap[item.icon] : HelpCircle;

  return (
    <NavLink
      to={item.href}
      title={item.label}
      end={item.href === "/dashboard" || item.href === "/settings"}
      className={() =>
        `relative group flex h-11 items-center transition-all duration-200 overflow-hidden flex-shrink-0 w-full ${
          isOpen ? "px-gutter-sm justify-start" : "justify-center px-0"
        } ${
          isActive
            ? "bg-foreground/5 text-primary border-l-2 border-sidebar-primary"
            : "text-muted-foreground border-l-2 border-transparent hover:bg-foreground/5 hover:text-sidebar-foreground"
        }`
      }
    >
      <Icon className="h-[18px] w-[18px] flex-shrink-0" strokeWidth={1.5} />
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: "auto", opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: luxuryEase }}
            className="overflow-hidden whitespace-nowrap"
          >
            <span className="ml-stack-sm text-xs uppercase tracking-wider block font-bold">
              {item.label}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
      {item.href === ADMIN_PATHS.notifications && (
        <UnreadBadge className={isOpen ? "ml-auto" : "absolute right-1 top-1"} />
      )}
    </NavLink>
  );
};

/**
 * A heading (CRM, CMS, Tools...) that opens and closes its links. Closed
 * headings are remembered; going to one of a heading's pages opens it.
 * With the sidebar shrunk to icons there are no headings, so every icon shows.
 */
const NavGroup = ({ group, isOpen }: { group: AdminNavItem; isOpen: boolean }) => {
  const { children, expanded, listId, toggle } = useNavGroupController(group, isOpen);

  return (
    <div className="flex flex-col w-full">
      {isOpen ? (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={expanded}
          aria-controls={listId}
          className="flex h-11 w-full items-center justify-between px-gutter-sm pt-stack-xs text-muted-foreground hover:text-sidebar-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sidebar-ring"
        >
          <span className="text-xs font-black uppercase tracking-wider">{group.label}</span>
          <motion.span
            initial={false}
            animate={{ rotate: expanded ? 0 : -90 }}
            transition={{ duration: 0.3, ease: luxuryEase }}
            className="flex"
            aria-hidden
          >
            <ChevronDown className="h-[14px] w-[14px]" strokeWidth={1.5} />
          </motion.span>
        </button>
      ) : (
        <div className="h-px bg-sidebar-border my-stack-xs w-2/3 mx-auto" />
      )}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            id={listId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: luxuryEase }}
            className="flex flex-col space-y-stack-xs w-full overflow-hidden"
          >
            {isOpen && group.label === "Accounting" && (
              <div className="px-gutter-sm pb-stack-xs">
                <BooksPicker />
              </div>
            )}
            {children.map((child) => (
              <NavItem key={child.href} item={child} isOpen={isOpen} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const AppSidebar: React.FC = () => {
  const { email, isOpen, userInitial, filteredItems, isPending, toggleSidebar, logout } = useAppSidebarController();

  return (
    <motion.aside
      initial={false}
      animate={{ width: isOpen ? "240px" : "72px" }}
      transition={{ duration: 0.5, ease: luxuryEase }}
      className="hidden sm:flex flex-col flex-shrink-0 py-gutter-sm z-30 relative bg-sidebar"
    >
      {/* 1. Toggle Header */}
      <div
        className={`relative flex w-full h-11 mb-stack-lg flex-shrink-0 ${
          isOpen ? "justify-end pr-gutter-sm" : "justify-center"
        }`}
      >
        <button
          type="button"
          onClick={toggleSidebar}
          aria-label={isOpen ? "Collapse menu" : "Expand menu"}
          aria-expanded={isOpen}
          className="relative h-11 w-11 hover:text-sidebar-primary transition-all duration-300 cursor-pointer flex items-center justify-center text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sidebar-ring"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <line x1="3" y1="6" x2="21" y2="6" />
            <motion.line
              x1="3"
              y1="12"
              x2="21"
              y2="12"
              animate={{ scaleX: isOpen ? 0.5 : 1 }}
              style={{ originX: 1 }}
              transition={{ duration: 0.4, ease: luxuryEase }}
            />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      </div>

      {/* 2. Main Navigation Area */}
      <nav className="flex w-full flex-1 flex-col space-y-stack-xs overflow-y-auto no-scrollbar">
        {filteredItems.map((item) => (
          <React.Fragment key={item.label}>
            {item.children && item.children.length > 0 ? (
              <NavGroup group={item} isOpen={isOpen} />
            ) : (
              <NavItem item={item} isOpen={isOpen} />
            )}
          </React.Fragment>
        ))}
      </nav>

      {/* 3. Footer / User Actions */}
      <div
        className={`mt-auto flex flex-col pt-stack-md w-full flex-shrink-0 ${
          isOpen ? "px-gutter-sm" : "items-center px-0"
        }`}
      >
        <button
          onClick={logout}
          disabled={isPending}
          className={`flex items-center rounded-none bg-foreground/5 text-sidebar-foreground/80 hover:bg-destructive/10 hover:text-destructive transition-colors border border-sidebar-border overflow-hidden flex-shrink-0 ${
            isOpen
              ? "w-full h-12 px-stack-sm justify-start"
              : "w-11 h-11 justify-center"
          }`}
        >
          <div className="flex h-6 w-6 items-center justify-center rounded-none bg-foreground/5 text-xs font-bold text-muted-foreground flex-shrink-0">
            {isPending ? (
              <LogOut className="h-[14px] w-[14px] animate-pulse" />
            ) : (
              userInitial
            )}
          </div>
          {isOpen && (
            <div className="ml-stack-sm flex flex-col justify-center items-start overflow-hidden text-left">
              <span className="text-xs uppercase tracking-wider block font-bold whitespace-nowrap">
                Sign out
              </span>
              <span className="text-xs font-normal text-muted-foreground truncate w-[130px]">
                {email}
              </span>
            </div>
          )}
        </button>
      </div>
    </motion.aside>
  );
};